/**
 * RouteExecutionPage - Pagina de Execucao e Monitoramento de Rotas
 *
 * Funcionalidades:
 * - Monitoramento em tempo real via WebSocket
 * - ETA dinamico baseado na posicao atual
 * - Deteccao de desvios de rota
 * - Sistema de alertas
 * - Marcar paradas como concluidas
 */

import {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Collapse,
  LinearProgress,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  CircularProgress,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  Notifications as AlertIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Navigation as NavigationIcon,
  LocalShipping as VehicleIcon,
  AccessTime as TimeIcon,
  Straighten as DistanceIcon,
  SkipNext as SkipIcon,
  Done as DoneIcon,
  Route as ReRouteIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';

import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import MapView from '../map/core/MapView';

import useRouteMonitor, { useExecutionState } from './hooks/useRouteMonitor';
import { EXECUTION_STATUS, ALERT_SEVERITY } from './services/routeMonitoringService';
import {
  reRouteFromCurrentPosition,
  createReRoutingHistory,
  REROUTE_REASON,
  REROUTE_REASON_LABELS,
} from './services/reRoutingService';
import { formatDistance, formatDuration } from '../common/services/routingService';
import routePlanService from '../common/services/routePlanService';

import { RoutePlanningMap, ProofOfDeliveryDialog, CostSummaryCard } from './components';
import { loadCostConfig } from './services/costCalculationService';

// =======================================
// Componentes Auxiliares
// =======================================

const StatusBadge = ({ status, tokens }) => {
  const statusConfig = {
    [EXECUTION_STATUS.NOT_STARTED]: { label: 'Nao Iniciada', color: tokens?.text?.tertiary },
    [EXECUTION_STATUS.IN_PROGRESS]: { label: 'Em Execucao', color: tokens?.semantic?.success },
    [EXECUTION_STATUS.PAUSED]: { label: 'Pausada', color: tokens?.semantic?.warning },
    [EXECUTION_STATUS.COMPLETED]: { label: 'Concluida', color: tokens?.accent?.primary },
    [EXECUTION_STATUS.CANCELLED]: { label: 'Cancelada', color: tokens?.semantic?.error },
  };

  const config = statusConfig[status] || statusConfig[EXECUTION_STATUS.NOT_STARTED];

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: `${config.color}20`,
        color: config.color,
        fontWeight: 600,
        fontSize: '11px',
      }}
    />
  );
};

const ETADisplay = ({ eta, tokens }) => {
  if (!eta) return null;

  const now = new Date();
  const arrivalTime = eta.estimatedArrival;
  const isLate = arrivalTime < now;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TimeIcon sx={{ fontSize: 16, color: isLate ? tokens?.semantic?.error : tokens?.text?.tertiary }} />
      <Typography
        sx={{
          fontSize: '13px',
          color: isLate ? tokens?.semantic?.error : tokens?.text?.secondary,
          fontWeight: isLate ? 600 : 400,
        }}
      >
        ETA: {arrivalTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        {isLate && ' (atrasado)'}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: tokens?.text?.tertiary }}>
        ({formatDistance(eta.distance)})
      </Typography>
    </Box>
  );
};

// =======================================
// Componente Principal
// =======================================

const RouteExecutionPage = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const tokens = usePremiumTokens();

  // Redux
  const devices = useSelector((state) => state.devices.items, shallowEqual);

  // Estado
  const [plan, setPlan] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedRouteIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [alertsExpanded, setAlertsExpanded] = useState(true);
  const [stopsExpanded, setStopsExpanded] = useState(true);

  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [currentStopForProof, setCurrentStopForProof] = useState(null);
  const [isSavingProof, setIsSavingProof] = useState(false);

  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipStopId, setSkipStopId] = useState(null);
  const [skipReason, setSkipReason] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Re-roteirizacao
  const [reRouteDialogOpen, setReRouteDialogOpen] = useState(false);
  const [reRouteReason, setReRouteReason] = useState(REROUTE_REASON.MANUAL);
  const [isReRouting, setIsReRouting] = useState(false);
  const [reRouteResult, setReRouteResult] = useState(null);
  const [reRouteHistoryOpen, setReRouteHistoryOpen] = useState(false);
  const [reRoutingHistory, setReRoutingHistory] = useState(null);

  // Rota e veiculo selecionados
  const selectedRoute = routes[selectedRouteIndex];
  const selectedVehicle = vehicles.find((v) => v.id === selectedRoute?.vehicleId);

  // ID do veiculo no plano (para persistencia)
  const [planVehicleId, setPlanVehicleId] = useState(null);

  // Hook para recuperar estado de execucao salvo
  const {
    state: executionState,
    isLoading: isLoadingExecutionState,
    isInProgress: wasInProgress,
    isPaused: wasPaused,
  } = useExecutionState(planId);

  // Hook de monitoramento com persistencia
  const {
    status,
    progress,
    deviation,
    nextStopETA,
    alerts,
    completedStops,
    currentPosition,
    isActive,
    isComplete,
    isSyncing,
    syncError,
    start,
    stop,
    pause,
    resume,
    completeStop,
    skipStop,
    clearAlert,
    syncNow,
  } = useRouteMonitor(selectedRoute, selectedVehicle, {
    planId: planId ? parseInt(planId, 10) : null,
    planVehicleId,
    enablePersistence: !!planId,
    enableSync: !!planId,
    initialState: executionState,
  });

  // Estado para lista de planos (quando sem planId)
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showPlanList, setShowPlanList] = useState(!planId);

  // Resetar estados quando planId muda
  useEffect(() => {
    if (planId) {
      setShowPlanList(false);
      setIsLoading(true);
      setError(null);
      setPlan(null);
      setRoutes([]);
      setVehicles([]);
    } else {
      setShowPlanList(true);
      setIsLoading(true);
    }
  }, [planId]);

  // Carregar plano ou lista de planos
  useEffectAsync(async () => {
    // Se nao tiver planId, carregar lista de planos para execucao
    if (!planId) {
      try {
        const response = await fetch('/api/route-plans', {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Erro ao carregar planos');
        }
        const plans = await response.json();
        // Filtrar planos que podem ser executados (approved, executing, optimized)
        const executablePlans = plans.filter(
          (p) => ['approved', 'executing', 'optimized'].includes(p.status)
        );
        setAvailablePlans(executablePlans);
        setShowPlanList(true);
        setIsLoading(false);
      } catch (err) {
        console.error('[RouteExecutionPage] Error loading plans:', err);
        setError('Erro ao carregar lista de planos');
        setIsLoading(false);
      }
      return;
    }

    try {
      const summary = await routePlanService.loadCompletePlan(planId);

      setPlan(summary.plan);

      // Reconstruir rotas a partir dos dados salvos
      const loadedRoutes = (summary.vehicles || []).map((v) => {
        const stops = (summary.stops || []).filter((s) => s.vehicleId === v.id);

        return {
          vehicleId: v.deviceId,
          distance: v.distance || 0,
          duration: v.duration || 0,
          geometry: v.routeGeometry ? JSON.parse(v.routeGeometry) : null,
          steps: [
            { type: 'start', location: { lat: v.startLat, lng: v.startLng } },
            ...stops.map((s, idx) => ({
              type: 'job',
              id: s.id,
              name: s.name,
              address: s.address,
              location: { lat: s.latitude, lng: s.longitude },
              serviceTime: s.serviceTime || 300,
              priority: s.priority,
              status: s.status,
              estimatedArrival: s.estimatedArrival,
              optimizedOrder: idx + 1,
            })),
            { type: 'end', location: { lat: v.startLat, lng: v.startLng } },
          ],
        };
      });

      setRoutes(loadedRoutes);

      // Carregar dados dos veiculos
      const vehicleData = (summary.vehicles || []).map((v) => {
        const device = devices[v.deviceId];
        return {
          id: v.deviceId,
          planVehicleId: v.id, // ID na tabela route_plan_vehicles
          name: device?.name || `Veiculo ${v.deviceId}`,
          color: v.color,
          ...device,
        };
      });

      setVehicles(vehicleData);

      // Definir planVehicleId do primeiro veiculo (para persistencia)
      if (summary.vehicles?.length > 0) {
        setPlanVehicleId(summary.vehicles[0].id);
      }
    } catch (err) {
      console.error('[RouteExecutionPage] Error loading plan:', err);
      setError(`Erro ao carregar plano: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [planId, devices]);

  // Handlers
  const handleStartExecution = useCallback(() => {
    start();
    setSnackbar({ open: true, message: 'Execucao iniciada!', severity: 'success' });
  }, [start]);

  const handlePauseExecution = useCallback(() => {
    pause();
    setSnackbar({ open: true, message: 'Execucao pausada', severity: 'info' });
  }, [pause]);

  const handleResumeExecution = useCallback(() => {
    resume();
    setSnackbar({ open: true, message: 'Execucao retomada', severity: 'success' });
  }, [resume]);

  const handleStopExecution = useCallback(() => {
    stop();
    setSnackbar({ open: true, message: 'Execucao finalizada', severity: 'info' });
  }, [stop]);

  const handleOpenProofDialog = useCallback((stop) => {
    setCurrentStopForProof(stop);
    setProofDialogOpen(true);
  }, []);

  const handleCloseProofDialog = useCallback(() => {
    setProofDialogOpen(false);
    setCurrentStopForProof(null);
  }, []);

  const handleConfirmDelivery = useCallback(async (proofData) => {
    if (!currentStopForProof) return;

    setIsSavingProof(true);

    try {
      // Preparar dados do comprovante
      const deliveryProof = {
        stopId: currentStopForProof.id,
        stopName: currentStopForProof.name,
        receiverName: proofData.receiverName,
        receiverDocument: proofData.receiverDocument,
        notes: proofData.notes,
        planId,
        vehicleId: selectedVehicle?.id,
        completedAt: new Date().toISOString(),
        location: proofData.location,
      };

      // Upload de fotos se houver
      if (proofData.photos && proofData.photos.length > 0) {
        const maxPhotoSize = 10 * 1024 * 1024; // 10MB
        const validPhotoTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const photoUrls = [];
        for (const photo of proofData.photos) {
          if (photo.file) {
            if (photo.file.size > maxPhotoSize) {
              console.warn(`[DeliveryProof] Photo too large: ${(photo.file.size / 1024 / 1024).toFixed(1)}MB (max ${maxPhotoSize / 1024 / 1024}MB)`);
              continue;
            }
            if (!validPhotoTypes.includes(photo.file.type)) {
              console.warn(`[DeliveryProof] Invalid photo type: ${photo.file.type}`);
              continue;
            }
            const formData = new FormData();
            formData.append('file', photo.file);
            formData.append('stopId', currentStopForProof.id.toString());
            formData.append('type', 'delivery_photo');

            try {
              const uploadRes = await fetch(`/api/route-plan-stops/${currentStopForProof.id}/photo`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
              });
              if (uploadRes.ok) {
                const uploadedPhoto = await uploadRes.json();
                photoUrls.push(uploadedPhoto.url || uploadedPhoto.path);
              }
            } catch (uploadErr) {
              console.warn('[DeliveryProof] Photo upload error:', uploadErr);
            }
          }
        }
        deliveryProof.photoUrls = photoUrls;
      }

      // Upload de assinatura se houver
      if (proofData.signature) {
        try {
          // Converter base64 para blob
          const response = await fetch(proofData.signature);
          const blob = await response.blob();
          const maxSigSize = 2 * 1024 * 1024; // 2MB
          if (blob.size > maxSigSize) {
            console.warn(`[DeliveryProof] Signature too large: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
          } else {
            const formData = new FormData();
            formData.append('file', blob, 'signature.png');
            formData.append('stopId', currentStopForProof.id.toString());
            formData.append('type', 'signature');

            const uploadRes = await fetch(`/api/route-plan-stops/${currentStopForProof.id}/signature`, {
              method: 'POST',
              body: formData,
              credentials: 'include',
            });
            if (uploadRes.ok) {
              const uploadedSignature = await uploadRes.json();
              deliveryProof.signatureUrl = uploadedSignature.url || uploadedSignature.path;
            }
          }
        } catch (sigErr) {
          console.warn('[DeliveryProof] Signature upload error:', sigErr);
        }
      }

      // Atualizar status da parada via API
      try {
        await routePlanService.updateStopStatus(currentStopForProof.id, 'completed', {
          receivedBy: proofData.receiverName,
          notes: proofData.notes,
          proofData: deliveryProof,
        });
      } catch (apiErr) {
        console.warn('[DeliveryProof] API update error:', apiErr);
      }

      // Marcar parada como concluida no estado local
      completeStop(currentStopForProof.id, proofData.notes || '');

      setProofDialogOpen(false);
      setCurrentStopForProof(null);
      setSnackbar({
        open: true,
        message: 'Entrega confirmada com sucesso!',
        severity: 'success',
      });
    } catch (err) {
      console.error('[DeliveryProof] Error:', err);
      setSnackbar({
        open: true,
        message: `Erro ao salvar prova de entrega: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsSavingProof(false);
    }
  }, [currentStopForProof, planId, selectedVehicle, completeStop]);

  const handleOpenSkipDialog = useCallback((stopId) => {
    setSkipStopId(stopId);
    setSkipReason('');
    setSkipDialogOpen(true);
  }, []);

  const handleSkipStop = useCallback(() => {
    if (skipStopId) {
      skipStop(skipStopId, skipReason);
      setSkipDialogOpen(false);
      setSnackbar({ open: true, message: 'Parada pulada', severity: 'warning' });
    }
  }, [skipStopId, skipReason, skipStop]);

  // Inicializar historico de re-roteirizacao
  useEffect(() => {
    if (planId) {
      const history = createReRoutingHistory(planId);
      setReRoutingHistory(history);
    }
  }, [planId]);

  // Handler de re-roteirizacao
  const handleReRoute = useCallback(async () => {
    if (!currentPosition || !selectedRoute || !selectedVehicle) {
      setSnackbar({
        open: true,
        message: 'Posicao atual nao disponivel para re-roteirizacao',
        severity: 'error',
      });
      return;
    }

    setIsReRouting(true);
    setReRouteResult(null);

    try {
      const result = await reRouteFromCurrentPosition({
        currentPosition,
        originalRoute: selectedRoute,
        vehicle: selectedVehicle,
        completedStopIds: completedStops,
        options: {
          reason: reRouteReason,
          useVROOM: true,
        },
      });

      if (result.success) {
        if (result.noMoreStops) {
          setSnackbar({
            open: true,
            message: 'Todas as entregas foram concluidas!',
            severity: 'info',
          });
          setReRouteDialogOpen(false);
        } else {
          setReRouteResult(result);

          // Adicionar ao historico
          if (reRoutingHistory) {
            reRoutingHistory.add({
              reason: reRouteReason,
              comparison: result.comparison,
              fromPosition: result.fromPosition,
              pendingStopsCount: result.pendingStopsCount,
            });
          }
        }
      }
    } catch (err) {
      console.error('[ReRoute] Error:', err);
      setSnackbar({
        open: true,
        message: `Erro na re-roteirizacao: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsReRouting(false);
    }
  }, [currentPosition, selectedRoute, selectedVehicle, completedStops, reRouteReason, reRoutingHistory]);

  // Aplicar nova rota
  const handleApplyNewRoute = useCallback(() => {
    if (!reRouteResult?.route) return;

    // Atualizar a rota no estado
    setRoutes((prev) => {
      const updated = [...prev];
      updated[selectedRouteIndex] = {
        ...updated[selectedRouteIndex],
        ...reRouteResult.route,
      };
      return updated;
    });

    setSnackbar({
      open: true,
      message: 'Nova rota aplicada com sucesso!',
      severity: 'success',
    });

    setReRouteDialogOpen(false);
    setReRouteResult(null);
  }, [reRouteResult, selectedRouteIndex]);

  // Paradas da rota atual
  const routeStops = useMemo(() => {
    if (!selectedRoute?.steps) return [];
    return selectedRoute.steps.filter((s) => s.type === 'job');
  }, [selectedRoute]);

  // Alertas nao lidos
  const activeAlerts = useMemo(() => {
    return alerts.filter((a) => !a.acknowledged);
  }, [alerts]);

  // Renderizar loading
  if (isLoading || isLoadingExecutionState) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress />
        {isLoadingExecutionState && (
          <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px' }}>
            Recuperando estado da execucao...
          </Typography>
        )}
      </Box>
    );
  }

  // Renderizar erro
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Erro</AlertTitle>
          {error}
        </Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  // Renderizar lista de planos para execucao
  if (showPlanList) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: tokens?.surface?.base,
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: tokens?.text?.secondary }}>
              <BackIcon />
            </IconButton>
            <Typography
              sx={{
                color: tokens?.text?.primary,
                fontWeight: 600,
                fontSize: '24px',
              }}
            >
              Execucao de Rotas
            </Typography>
          </Box>

          {availablePlans.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                backgroundColor: tokens?.surface?.raised,
                borderRadius: tokens?.radius?.lg,
                border: `1px solid ${tokens?.border?.subtle}`,
              }}
            >
              <NavigationIcon sx={{ fontSize: 48, color: tokens?.text?.tertiary, mb: 2 }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '16px', mb: 1 }}>
                Nenhum plano disponivel para execucao
              </Typography>
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px', mb: 3 }}>
                Crie e aprove um plano de roteirizacao para iniciar a execucao
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/settings/route-planning')}
                sx={{
                  backgroundColor: tokens?.accent?.primary,
                  '&:hover': { backgroundColor: tokens?.accent?.hover },
                }}
              >
                Criar Plano
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {availablePlans.map((p) => {
                const isExecuting = p.status === 'executing';
                const statusConfig = {
                  approved: { label: 'Aprovado', color: tokens?.semantic?.success },
                  executing: { label: 'Em Execucao - Clique para Continuar', color: tokens?.accent?.primary },
                  optimized: { label: 'Otimizado', color: tokens?.semantic?.warning },
                };
                const config = statusConfig[p.status] || { label: p.status, color: tokens?.text?.tertiary };

                return (
                  <Box
                    key={p.id}
                    component={Link}
                    to={`/settings/route-execution/${p.id}`}
                    sx={{
                      p: 3,
                      backgroundColor: isExecuting ? `${tokens?.accent?.primary}10` : tokens?.surface?.raised,
                      borderRadius: tokens?.radius?.lg,
                      border: `1px solid ${isExecuting ? tokens?.accent?.primary : tokens?.border?.subtle}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      display: 'block',
                      position: 'relative',
                      '&:hover': {
                        borderColor: tokens?.accent?.primary,
                        transform: 'translateY(-2px)',
                        boxShadow: tokens?.shadow?.md,
                      },
                    }}
                  >
                    {/* Indicador de execucao ativa */}
                    {isExecuting && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: tokens?.accent?.primary,
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography
                          sx={{
                            color: tokens?.text?.primary,
                            fontWeight: 600,
                            fontSize: '18px',
                          }}
                        >
                          {p.name}
                        </Typography>
                        {p.description && (
                          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px', mt: 0.5 }}>
                            {p.description}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          backgroundColor: `${config.color}20`,
                          color: config.color,
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 3 }}>
                      {p.totalDistance > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DistanceIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
                          <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                            {formatDistance(p.totalDistance)}
                          </Typography>
                        </Box>
                      )}
                      {p.totalDuration > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
                          <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                            {formatDuration(p.totalDuration)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NavigationIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
                        <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                          {p.date ? new Date(p.date).toLocaleDateString('pt-BR') : 'Sem data'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: tokens?.surface?.base,
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: 400,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: tokens?.surface?.base,
            borderRight: `1px solid ${tokens?.border?.subtle}`,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${tokens?.border?.subtle}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton
                size="small"
                onClick={() => navigate(-1)}
                sx={{ color: tokens?.text?.secondary }}
              >
                <BackIcon />
              </IconButton>
              <Typography
                sx={{
                  color: tokens?.text?.primary,
                  fontWeight: 600,
                  fontSize: '18px',
                  flex: 1,
                }}
              >
                {plan?.name || 'Execucao de Rota'}
              </Typography>
              {isSyncing && (
                <Tooltip title="Sincronizando...">
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                </Tooltip>
              )}
              <StatusBadge status={status} tokens={tokens} />
            </Box>

            {/* Indicador de execucao recuperada */}
            {(wasInProgress || wasPaused) && status === EXECUTION_STATUS.NOT_STARTED && (
              <Alert
                severity="info"
                sx={{ mb: 2, fontSize: '13px' }}
                action={
                  <Button color="inherit" size="small" onClick={syncNow}>
                    Atualizar
                  </Button>
                }
              >
                Esta rota tem uma execucao {wasPaused ? 'pausada' : 'em andamento'}.
                Clique em &quot;Retomar&quot; para continuar de onde parou.
              </Alert>
            )}

            {/* Erro de sincronizacao */}
            {syncError && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: '13px' }}>
                Erro ao sincronizar: {syncError}
              </Alert>
            )}

            {/* Controles de execucao */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {status === EXECUTION_STATUS.NOT_STARTED && (
                <Button
                  variant="contained"
                  startIcon={<StartIcon />}
                  onClick={handleStartExecution}
                  fullWidth
                  sx={{
                    backgroundColor: tokens?.semantic?.success,
                    '&:hover': { backgroundColor: '#16a34a' },
                  }}
                >
                  Iniciar Execucao
                </Button>
              )}

              {status === EXECUTION_STATUS.IN_PROGRESS && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<PauseIcon />}
                    onClick={handlePauseExecution}
                    sx={{ flex: 1, borderColor: tokens?.border?.default }}
                  >
                    Pausar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<StopIcon />}
                    onClick={handleStopExecution}
                    color="error"
                    sx={{ flex: 1 }}
                  >
                    Finalizar
                  </Button>
                </>
              )}

              {status === EXECUTION_STATUS.PAUSED && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<StartIcon />}
                    onClick={handleResumeExecution}
                    sx={{
                      flex: 1,
                      backgroundColor: tokens?.semantic?.success,
                      '&:hover': { backgroundColor: '#16a34a' },
                    }}
                  >
                    Retomar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<StopIcon />}
                    onClick={handleStopExecution}
                    color="error"
                    sx={{ flex: 1 }}
                  >
                    Finalizar
                  </Button>
                </>
              )}

              {status === EXECUTION_STATUS.COMPLETED && (
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  fullWidth
                  sx={{ borderColor: tokens?.border?.default }}
                >
                  Voltar ao Planejamento
                </Button>
              )}
            </Box>

            {/* Progresso */}
            {progress && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '12px', color: tokens?.text?.secondary }}>
                    Progresso
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: tokens?.text?.primary, fontWeight: 600 }}>
                    {progress.completedStops}/{progress.totalStops} paradas
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress.progress ?? progress.percent ?? 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: tokens?.surface?.elevated,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: tokens?.accent?.primary,
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {/* ETA proxima parada */}
            {nextStopETA && isActive && (
              <Box sx={{ mt: 2 }}>
                <ETADisplay eta={nextStopETA} tokens={tokens} />
              </Box>
            )}

            {/* Indicador de desvio */}
            {deviation?.deviated && isActive && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                icon={<WarningIcon />}
              >
                Veiculo desviou {Math.round(deviation.distance)}m da rota
              </Alert>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Alertas */}
            {activeAlerts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box
                  onClick={() => setAlertsExpanded(!alertsExpanded)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge badgeContent={activeAlerts.length} color="error">
                      <AlertIcon sx={{ color: tokens?.semantic?.warning }} />
                    </Badge>
                    <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
                      Alertas
                    </Typography>
                  </Box>
                  {alertsExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </Box>

                <Collapse in={alertsExpanded}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {activeAlerts.map((alert) => (
                      <Alert
                        key={alert.id}
                        severity={alert.severity === ALERT_SEVERITY.CRITICAL || alert.severity === ALERT_SEVERITY.ERROR ? 'error' : alert.severity === ALERT_SEVERITY.WARNING ? 'warning' : 'info'}
                        onClose={() => clearAlert(alert.id)}
                        sx={{ fontSize: '13px' }}
                      >
                        {alert.message}
                      </Alert>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            <Divider sx={{ borderColor: tokens?.border?.subtle, my: 2 }} />

            {/* Lista de paradas */}
            <Box>
              <Box
                onClick={() => setStopsExpanded(!stopsExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NavigationIcon sx={{ color: tokens?.text?.secondary }} />
                  <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
                    Paradas ({routeStops.length})
                  </Typography>
                </Box>
                {stopsExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </Box>

              <Collapse in={stopsExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {routeStops.map((stop, index) => {
                    const isCompleted = completedStops.includes(stop.id);
                    const isCurrent = progress?.currentStopIndex === index && !isCompleted;

                    return (
                      <Box
                        key={stop.id}
                        sx={{
                          p: 2,
                          borderRadius: tokens?.radius?.md,
                          backgroundColor: isCurrent
                            ? `${tokens?.accent?.primary}15`
                            : isCompleted
                            ? `${tokens?.semantic?.success}10`
                            : tokens?.surface?.raised,
                          border: `1px solid ${
                            isCurrent
                              ? tokens?.accent?.primary
                              : isCompleted
                              ? tokens?.semantic?.success
                              : tokens?.border?.subtle
                          }`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          {/* Numero/Status */}
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isCompleted
                                ? tokens?.semantic?.success
                                : isCurrent
                                ? tokens?.accent?.primary
                                : tokens?.surface?.elevated,
                              color: isCompleted || isCurrent ? '#fff' : tokens?.text?.secondary,
                              fontSize: '12px',
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          >
                            {isCompleted ? <DoneIcon sx={{ fontSize: 16 }} /> : index + 1}
                          </Box>

                          {/* Info */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: tokens?.text?.primary,
                                fontWeight: 500,
                                fontSize: '14px',
                              }}
                            >
                              {stop.name || `Parada ${index + 1}`}
                            </Typography>
                            <Typography
                              sx={{
                                color: tokens?.text?.tertiary,
                                fontSize: '12px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {stop.address || `${stop.location?.lat?.toFixed(5)}, ${stop.location?.lng?.toFixed(5)}`}
                            </Typography>

                            {/* ETA para parada atual */}
                            {isCurrent && nextStopETA && (
                              <Box sx={{ mt: 1 }}>
                                <ETADisplay eta={nextStopETA} tokens={tokens} />
                              </Box>
                            )}
                          </Box>

                          {/* Acoes */}
                          {isActive && isCurrent && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Concluir com Prova de Entrega">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenProofDialog(stop)}
                                  sx={{ color: tokens?.semantic?.success }}
                                >
                                  <CompleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Pular">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenSkipDialog(stop.id)}
                                  sx={{ color: tokens?.semantic?.warning }}
                                >
                                  <SkipIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>

            {/* Card de Custos */}
            {selectedRoute && (
              <Box sx={{ mt: 2 }}>
                <CostSummaryCard
                  singleRoute={selectedRoute}
                  costConfig={loadCostConfig()}
                  compact
                  showBreakdown
                  showMetrics
                  title="Custo da Rota"
                />
              </Box>
            )}
          </Box>

          {/* Footer - Info do veiculo e Re-roteirizacao */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${tokens?.border?.subtle}`,
              backgroundColor: tokens?.surface?.raised,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <VehicleIcon sx={{ color: tokens?.accent?.primary }} />
                <Box>
                  <Typography sx={{ color: tokens?.text?.primary, fontWeight: 500, fontSize: '14px' }}>
                    {selectedVehicle?.name || 'Veiculo'}
                  </Typography>
                  {currentPosition && (
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                      Velocidade: {Math.round(currentPosition.speed || 0)} km/h
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Botao de historico */}
              {reRoutingHistory && reRoutingHistory.getCount() > 0 && (
                <Tooltip title={`${reRoutingHistory.getCount()} re-roteirizacoes`}>
                  <IconButton
                    size="small"
                    onClick={() => setReRouteHistoryOpen(true)}
                    sx={{ color: tokens?.text?.tertiary }}
                  >
                    <Badge badgeContent={reRoutingHistory.getCount()} color="primary">
                      <HistoryIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Botao de re-roteirizacao */}
            {isActive && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ReRouteIcon />}
                onClick={() => setReRouteDialogOpen(true)}
                disabled={!currentPosition}
                sx={{
                  borderColor: tokens?.accent?.primary,
                  color: tokens?.accent?.primary,
                  '&:hover': {
                    backgroundColor: `${tokens?.accent?.primary}10`,
                    borderColor: tokens?.accent?.hover,
                  },
                }}
              >
                Re-roteirizar
              </Button>
            )}
          </Box>
        </Box>

        {/* Mapa */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <MapView>
            <RoutePlanningMap
              deliveries={routeStops.map((s) => ({
                id: s.id,
                name: s.name,
                address: s.address,
                lat: s.location?.lat,
                lng: s.location?.lng,
                routeColor: selectedVehicle?.color || '#00d4aa',
              }))}
              vehicles={selectedVehicle ? [selectedVehicle] : []}
              optimizedRoutes={selectedRoute ? [selectedRoute] : []}
              selectedRouteIndex={0}
            />
          </MapView>

          {/* Status overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: tokens?.surface?.glass,
              backdropFilter: 'blur(12px)',
              borderRadius: tokens?.radius?.lg,
              border: `1px solid ${tokens?.border?.subtle}`,
              p: 2,
              minWidth: 200,
            }}
          >
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '11px', mb: 1 }}>
              STATUS EM TEMPO REAL
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: isActive ? tokens?.semantic?.success : tokens?.text?.tertiary,
                    animation: isActive ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px' }}>
                  {isActive ? 'Monitorando' : isComplete ? 'Concluido' : 'Aguardando'}
                </Typography>
              </Box>

              {progress && (
                <>
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
                    {progress.completedStops} de {progress.totalStops} entregas
                  </Typography>
                  {selectedRoute && (
                    <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
                      {formatDistance(selectedRoute.distance)} total
                    </Typography>
                  )}
                </>
              )}

              {deviation && !deviation.deviated && isActive && (
                <Typography sx={{ color: tokens?.semantic?.success, fontSize: '12px' }}>
                  Na rota planejada
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dialog Prova de Entrega */}
      <ProofOfDeliveryDialog
        open={proofDialogOpen}
        onClose={handleCloseProofDialog}
        onConfirm={handleConfirmDelivery}
        stop={currentStopForProof}
        isLoading={isSavingProof}
        requireSignature={true}
        requirePhoto={false}
        maxPhotos={5}
      />

      {/* Dialog Pular Parada */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Pular Parada</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder="Informe o motivo para pular esta parada..."
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSkipStop}
            variant="contained"
            color="warning"
            disabled={!skipReason.trim()}
          >
            Pular Parada
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Re-roteirizacao */}
      <Dialog
        open={reRouteDialogOpen}
        onClose={() => {
          setReRouteDialogOpen(false);
          setReRouteResult(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReRouteIcon sx={{ color: tokens?.accent?.primary }} />
          Re-roteirizar
        </DialogTitle>
        <DialogContent>
          {!reRouteResult ? (
            <>
              <Typography sx={{ color: tokens?.text?.secondary, mb: 2 }}>
                Recalcular a rota a partir da posicao atual do veiculo, considerando apenas as entregas pendentes.
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Motivo da re-roteirizacao</InputLabel>
                <Select
                  value={reRouteReason}
                  onChange={(e) => setReRouteReason(e.target.value)}
                  label="Motivo da re-roteirizacao"
                >
                  {Object.entries(REROUTE_REASON_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {currentPosition && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Posicao atual: {currentPosition.latitude?.toFixed(5)}, {currentPosition.longitude?.toFixed(5)}
                </Alert>
              )}

              {progress && (
                <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '13px' }}>
                  Paradas restantes: {progress.remainingStops} de {progress.totalStops}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Nova rota calculada com sucesso!
              </Alert>

              {/* Comparacao */}
              <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 1 }}>
                Comparacao com rota atual:
              </Typography>

              <Box
                sx={{
                  backgroundColor: tokens?.surface?.elevated,
                  borderRadius: tokens?.radius?.md,
                  p: 2,
                  mb: 2,
                }}
              >
                {/* Distancia */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                    Distancia
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px', textDecoration: 'line-through' }}>
                      {formatDistance(reRouteResult.comparison.originalDistance)}
                    </Typography>
                    <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
                      {formatDistance(reRouteResult.comparison.newDistance)}
                    </Typography>
                    {reRouteResult.comparison.distanceDiff !== 0 && (
                      <Chip
                        size="small"
                        icon={reRouteResult.comparison.distanceDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        label={`${reRouteResult.comparison.distanceDiff > 0 ? '+' : ''}${reRouteResult.comparison.distanceChangePercent}%`}
                        sx={{
                          backgroundColor: reRouteResult.comparison.distanceDiff > 0
                            ? `${tokens?.semantic?.error}20`
                            : `${tokens?.semantic?.success}20`,
                          color: reRouteResult.comparison.distanceDiff > 0
                            ? tokens?.semantic?.error
                            : tokens?.semantic?.success,
                          '& .MuiChip-icon': {
                            color: 'inherit',
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Duracao */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                    Tempo estimado
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px', textDecoration: 'line-through' }}>
                      {formatDuration(reRouteResult.comparison.originalDuration)}
                    </Typography>
                    <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
                      {formatDuration(reRouteResult.comparison.newDuration)}
                    </Typography>
                    {reRouteResult.comparison.durationDiff !== 0 && (
                      <Chip
                        size="small"
                        icon={reRouteResult.comparison.durationDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        label={`${reRouteResult.comparison.durationDiff > 0 ? '+' : ''}${reRouteResult.comparison.durationChangePercent}%`}
                        sx={{
                          backgroundColor: reRouteResult.comparison.durationDiff > 0
                            ? `${tokens?.semantic?.error}20`
                            : `${tokens?.semantic?.success}20`,
                          color: reRouteResult.comparison.durationDiff > 0
                            ? tokens?.semantic?.error
                            : tokens?.semantic?.success,
                          '& .MuiChip-icon': {
                            color: 'inherit',
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                Paradas pendentes: {reRouteResult.pendingStopsCount}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReRouteDialogOpen(false);
              setReRouteResult(null);
            }}
          >
            {reRouteResult ? 'Cancelar' : 'Fechar'}
          </Button>
          {!reRouteResult ? (
            <Button
              onClick={handleReRoute}
              variant="contained"
              disabled={isReRouting}
              startIcon={isReRouting ? <CircularProgress size={16} color="inherit" /> : <ReRouteIcon />}
              sx={{
                backgroundColor: tokens?.accent?.primary,
                '&:hover': { backgroundColor: tokens?.accent?.hover },
              }}
            >
              {isReRouting ? 'Calculando...' : 'Calcular Nova Rota'}
            </Button>
          ) : (
            <Button
              onClick={handleApplyNewRoute}
              variant="contained"
              color="success"
            >
              Aplicar Nova Rota
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Historico de Re-roteirizacoes */}
      <Dialog
        open={reRouteHistoryOpen}
        onClose={() => setReRouteHistoryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ color: tokens?.text?.secondary }} />
          Historico de Re-roteirizacoes
        </DialogTitle>
        <DialogContent>
          {reRoutingHistory && reRoutingHistory.getCount() > 0 ? (
            <>
              {/* Estatisticas */}
              <Box
                sx={{
                  backgroundColor: tokens?.surface?.elevated,
                  borderRadius: tokens?.radius?.md,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px', mb: 1 }}>
                  RESUMO
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '20px' }}>
                      {reRoutingHistory.getCount()}
                    </Typography>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                      re-roteirizacoes
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '20px' }}>
                      {formatDistance(Math.abs(reRoutingHistory.getStats().totalDistanceAdded))}
                    </Typography>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                      {reRoutingHistory.getStats().totalDistanceAdded >= 0 ? 'adicionado' : 'economizado'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Lista */}
              <List dense>
                {reRoutingHistory.getAll().reverse().map((entry) => (
                  <ListItem
                    key={entry.id}
                    sx={{
                      backgroundColor: tokens?.surface?.raised,
                      borderRadius: tokens?.radius?.sm,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      <ReRouteIcon sx={{ color: tokens?.text?.tertiary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={REROUTE_REASON_LABELS[entry.reason] || entry.reason}
                      secondary={
                        <>
                          {new Date(entry.timestamp).toLocaleString('pt-BR')}
                          {entry.comparison && (
                            <span style={{ marginLeft: 8 }}>
                              | {entry.comparison.distanceChangePercent > 0 ? '+' : ''}{entry.comparison.distanceChangePercent}% distancia
                            </span>
                          )}
                        </>
                      }
                      primaryTypographyProps={{
                        sx: { color: tokens?.text?.primary, fontSize: '14px' },
                      }}
                      secondaryTypographyProps={{
                        sx: { color: tokens?.text?.tertiary, fontSize: '12px' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Typography sx={{ color: tokens?.text?.tertiary, textAlign: 'center', py: 3 }}>
              Nenhuma re-roteirizacao realizada ainda.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReRouteHistoryOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RouteExecutionPage;
